import { useState } from "react";
import { Button, HBLoadingSpinnerOnly, Modal, SelectField } from "~components";
import { Css } from "~generated/css";
import { AggregateVersionStatus, CheckoutConfigStatus, Maybe } from "~generated/graphqlTypes";
import { queryResult } from "~utils";
import {
  DebugModal_VersionFragment,
  useDebugModal_LoadQuery,
  useDebugModal_SyncToVersionMutation,
} from "./DebugModal.generated";

type DebugModalProps = {
  checkoutConfigId: string;
  onClose: () => void;
};

export function DebugModal({ checkoutConfigId, onClose }: DebugModalProps) {
  const query = useDebugModal_LoadQuery({ variables: { id: checkoutConfigId } });

  return (
    <Modal isOpen close={onClose} title="Config Details" size={{ width: "560px", height: "auto" }}>
      {queryResult(query, ({ checkoutConfig: config }) => {
        const { productOffering: pof, structure } = config;
        return (
          <div css={Css.df.fdc.px3.pt2.pb4.$}>
            <DebugLinkRow name="Pricing Trace" url={config.pricingTraceUrl?.url} />
            <DebugLinkRow label="Structure" name={structure.name} url={structure.blueprintUrl.url} />
            {pof && (
              <>
                <DebugLinkRow label="Product Offering" name={pof.name} url={pof.blueprintUrl.url} />
                <div css={Css.pl3.df.fdc.$}>
                  <VersionSelector
                    checkoutConfigId={checkoutConfigId}
                    currentVersion={config.aggregateVersion}
                    versions={pof.aggregateVersions}
                    aggregateCheckoutId={pof.aggregateCheckout?.id}
                    disabled={
                      config.status === CheckoutConfigStatus.Reserved || config.status === CheckoutConfigStatus.Expired
                    }
                  />
                  <DebugLinkRow
                    label="Plan Package"
                    name={pof.planPackage.name}
                    url={pof.planPackage.blueprintUrl.url}
                  />
                  <DebugLinkRow
                    label="Interior Design Package"
                    name={pof.interiorDesignPackage.name}
                    url={pof.interiorDesignPackage.blueprintUrl.url}
                  />
                  <DebugLinkRow
                    label="Exterior Design Package"
                    name={pof.exteriorDesignPackage.name}
                    url={pof.exteriorDesignPackage.blueprintUrl.url}
                  />
                </div>
              </>
            )}
          </div>
        );
      })}
    </Modal>
  );
}

type VersionSelectorProps = {
  checkoutConfigId: string;
  currentVersion: Maybe<DebugModal_VersionFragment>;
  versions: DebugModal_VersionFragment[];
  aggregateCheckoutId: string | undefined;
  disabled: boolean;
};

function VersionSelector({
  checkoutConfigId,
  currentVersion,
  versions,
  aggregateCheckoutId,
  disabled,
}: VersionSelectorProps) {
  const [syncError, setSyncError] = useState<string>();
  const [syncToVersion, { loading: syncing }] = useDebugModal_SyncToVersionMutation({
    refetchQueries: ["ConfiguratorContext_LoadConfig", "DebugModal_Load"],
    onError: (err) => setSyncError(err.message),
    onCompleted: () => setSyncError(undefined),
  });

  // Hide versions older than the promoted checkout version, but always keep the ccfg's current version.
  const checkoutVersionNumber = versions.find((v) => v.id === aggregateCheckoutId)?.version ?? 0;
  const eligibleVersions = versions.filter(
    (v) => (v.version ?? 0) >= checkoutVersionNumber || v.id === currentVersion?.id,
  );

  const versionOptions = eligibleVersions.map((v) => {
    const isCheckout = v.id === aggregateCheckoutId;
    const label = v.status.code === AggregateVersionStatus.Draft ? `Draft of v${v.version}` : `v${v.version}`;
    const promotedInfo = v.promotedAt
      ? `Promoted on ${new Date(v.promotedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}${v.promotedBy ? ` by ${v.promotedBy.name}` : ""}`
      : undefined;
    const value = isCheckout ? `${label} (promoted)` : label;
    return { id: v.id, value, info: promotedInfo };
  });

  const currentLabel = versionOptions.find((o) => o.id === currentVersion?.id)?.value ?? "";

  async function handleChange(value: string) {
    const rpavId = versionOptions.find((o) => o.value === value)?.id;
    if (!rpavId || syncing || rpavId === currentVersion?.id) return;
    setSyncError(undefined);
    await syncToVersion({ variables: { input: { id: checkoutConfigId, rpavId } } });
  }

  return (
    <div css={Css.df.fdc.py1.bb.bcGray200.$}>
      <SelectField
        label={
          <div css={Css.df.aic.gap1.t12.gray.ttn.$}>
            <span>Version</span>
            {syncing && (
              <div css={Css.wPx(16).hPx(16).$}>
                <HBLoadingSpinnerOnly />
              </div>
            )}
          </div>
        }
        options={versionOptions}
        selectedOptions={currentLabel}
        onChange={handleChange}
        allowDeselect={false}
        disabled={disabled}
      />
      {syncError && <div css={Css.t12.red600.mt1.$}>{syncError}</div>}
    </div>
  );
}

type DebugLinkRowProps = { label?: string; name?: string; url: string | undefined };

function DebugLinkRow({ label, name, url }: DebugLinkRowProps) {
  if (!url) return null;
  return (
    <div css={Css.df.fdc.py1.bb.bcGray200.$}>
      <span css={Css.t12.gray.$}>{label}</span>
      <Button variant="link" href={url} openInNewTab xss={Css.jcfs.t14.fw500.pl0.$}>
        {name ?? label}
      </Button>
    </div>
  );
}
